/**
 * Centralized tracking event definitions for PostHog analytics
 * This ensures consistent event naming and properties across the application
 */

// Authentication Events
export const AUTH_EVENTS = {
  LOGIN: 'user_logged_in',
  LOGOUT: 'user_logged_out',
  REGISTER: 'user_registered',
  TENANT_SWITCH: 'tenant_switched',
  MFA_COMPLETED: 'mfa_completed',
  PASSWORD_RESET: 'password_reset_requested',
  PASSWORD_CHANGED: 'password_changed',
} as const;

// Survey Management Events
export const SURVEY_EVENTS = {
  CREATED: 'survey_created',
  PUBLISHED: 'survey_published',
  EDITED: 'survey_edited',
  DELETED: 'survey_deleted',
  DUPLICATED: 'survey_duplicated',
  CLOSED: 'survey_closed',
  REOPENED: 'survey_reopened',
  STATUS_CHANGED: 'survey_status_changed',
  PREVIEW_OPENED: 'survey_preview_opened',
  QUESTION_ADDED: 'question_added',
  QUESTION_REMOVED: 'question_removed',
  QUESTION_EDITED: 'question_edited',
  QUESTION_DELETED: 'question_deleted',
  QUESTION_REORDERED: 'question_reordered',
  QUESTION_TYPE_SELECTED: 'question_type_selected',
  SETTINGS_UPDATED: 'survey_settings_updated',
} as const;

// Survey Instance Events
export const SURVEY_INSTANCE_EVENTS = {
  INSTANCE_CREATED: 'survey_instance_created',
  INSTANCE_SELECTED: 'survey_instance_selected',
  INSTANCE_QR_DISPLAYED: 'survey_instance_qr_displayed',
  INSTANCE_REFRESHED: 'survey_instance_refreshed',
  INSTANCE_LIST_VIEWED: 'survey_instance_list_viewed',
  INSTANCE_ACCESS_LINK_COPIED: 'survey_instance_link_copied',
} as const;

// Response Collection Events
export const RESPONSE_EVENTS = {
  STARTED: 'survey_started',
  COMPLETED: 'survey_completed',
  ABANDONED: 'survey_abandoned',
  SUBMITTED: 'response_submitted',
  SAVED_DRAFT: 'response_draft_saved',
  FILE_UPLOADED: 'file_uploaded',
  PAGE_CHANGED: 'survey_page_changed',
  QUESTION_ANSWERED: 'question_answered',
} as const;

// Survey Response Journey Events
export const SURVEY_RESPONSE_EVENTS = {
  // Entry & Discovery
  QR_SCANNED: 'survey_qr_scanned',
  LINK_ACCESSED: 'survey_link_accessed',

  // Session Management
  SESSION_CREATED: 'anonymous_session_created',
  SESSION_RESUMED: 'survey_session_resumed',
  SESSION_EXPIRED: 'survey_session_expired',

  // Survey Flow
  SURVEY_STARTED: 'survey_response_started',
  ANSWER_SUBMITTED: 'survey_answer_submitted',
  COMMENT_ADDED: 'survey_comment_added',
  VALIDATION_ERROR: 'survey_validation_error',

  // Progress Milestones
  PROGRESS_MILESTONE: 'survey_progress_milestone',

  // Language & Accessibility
  LANGUAGE_SWITCHED: 'survey_language_switched',

  // Completion & Abandonment
  SURVEY_COMPLETED: 'survey_response_completed',
  SURVEY_ABANDONED: 'survey_response_abandoned',
  ERROR_ENCOUNTERED: 'survey_error_encountered',
} as const;

// Report & Analytics Events
export const REPORT_EVENTS = {
  GENERATED: 'report_generated',
  EXPORTED: 'report_exported',
  VIEWED: 'report_viewed',
  FILTER_APPLIED: 'report_filter_applied',
  METRIC_VIEWED: 'metric_viewed',
  CHART_INTERACTED: 'chart_interaction',
  DASHBOARD_VIEWED: 'dashboard_viewed',
} as const;

// Report Management Events
export const REPORT_MANAGEMENT_EVENTS = {
  REPORT_SELECTED: 'report_selected',
  AUDIT_NAVIGATION_CLICKED: 'audit_navigation_clicked',
  REPORT_GENERATION_COMPLETED: 'report_generation_completed',
} as const;

// Report Viewing & Interaction Events
export const REPORT_VIEWING_EVENTS = {
  REPORT_VIEWED: 'report_viewed',
  COMMENT_EXPANDED: 'comment_expanded',
  PDF_EXPORT_CLICKED: 'pdf_export_clicked',
  PDF_EXPORT_COMPLETED: 'pdf_export_completed',
} as const;

// Report Performance & Quality Events
export const REPORT_ANALYTICS_EVENTS = {
  REPORT_ENGAGEMENT_TRACKED: 'report_engagement_tracked',
  QUESTION_VIEW_DURATION: 'question_view_duration',
  LARGE_DATASET_RENDERED: 'large_dataset_rendered',
  REPORT_SESSION_SUMMARY: 'report_session_summary',
} as const;

// Survey Audit Events
export const AUDIT_EVENTS = {
  AUDITS_PAGE_VIEWED: 'audits_page_viewed',
  AUDIT_STARTED: 'audit_started',
  AUDIT_CONTINUED: 'audit_continued',
  AUDIT_COMPLETED: 'audit_completed',
  AUDIT_PUBLISHED: 'audit_published',
  AUDIT_ARCHIVED: 'audit_archived',
  AUDIT_UNARCHIVED: 'audit_unarchived',
  AUDIT_VIEWED: 'audit_viewed',
  AUDIT_TRAIL_VIEWED: 'audit_trail_viewed',
  AUDIT_RESPONSE_INCLUDED: 'audit_response_included',
  AUDIT_RESPONSE_EXCLUDED: 'audit_response_excluded',
  AUDIT_COMMENT_INCLUDED: 'audit_comment_included',
  AUDIT_COMMENT_EXCLUDED: 'audit_comment_excluded',
} as const;

// User Management Events
export const USER_EVENTS = {
  INVITED: 'user_invited',
  ROLE_ASSIGNED: 'role_assigned',
  PERMISSION_MODIFIED: 'permission_modified',
  DEACTIVATED: 'user_deactivated',
  ACTIVATED: 'user_activated',
  PROFILE_UPDATED: 'profile_updated',
  SETTINGS_CHANGED: 'settings_changed',
} as const;

// Feature Usage Events
export const FEATURE_EVENTS = {
  ACCESSED: 'feature_accessed',
  FIRST_USE: 'feature_first_use',
  TUTORIAL_STARTED: 'tutorial_started',
  TUTORIAL_COMPLETED: 'tutorial_completed',
  HELP_OPENED: 'help_opened',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
} as const;

// Navigation Events
export const NAVIGATION_EVENTS = {
  PAGE_VIEWED: 'page_viewed',
  MODULE_OPENED: 'module_opened',
  TAB_SWITCHED: 'tab_switched',
  BREADCRUMB_CLICKED: 'breadcrumb_clicked',
  MENU_CLICKED: 'menu_clicked',
  SEARCH_PERFORMED: 'search_performed',
  SURVEY_BUILDER_OPENED: 'survey_builder_opened',
  SURVEY_NAVIGATION_CLICKED: 'survey_navigation_clicked',
} as const;

// Domain/Institute Events
export const DOMAIN_EVENTS = {
  CREATED: 'domain_created',
  EDITED: 'domain_edited',
  DELETED: 'domain_deleted',
  USER_ADDED: 'domain_user_added',
  USER_REMOVED: 'domain_user_removed',
} as const;

// Subject/Course Events
export const SUBJECT_EVENTS = {
  CREATED: 'subject_created',
  EDITED: 'subject_edited',
  DELETED: 'subject_deleted',
  ENROLLED: 'subject_enrolled',
  UNENROLLED: 'subject_unenrolled',
  ACTIVATED: 'subject_activated',
  DEACTIVATED: 'subject_deactivated',
} as const;

// Error Events (complementary to PostHog exception tracking)
export const ERROR_EVENTS = {
  OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',
  NETWORK_ERROR: 'network_error',
  PERMISSION_ERROR: 'permission_error',
} as const;

// Performance Events
export const PERFORMANCE_EVENTS = {
  SLOW_API: 'slow_api_call',
  SLOW_RENDER: 'slow_page_render',
  MEMORY_WARNING: 'memory_warning',
  CRASH: 'app_crash',
} as const;

/**
 * All tracking events grouped by category
 */
export const TRACKING_EVENTS = {
  AUTH: AUTH_EVENTS,
  SURVEY: SURVEY_EVENTS,
  SURVEY_INSTANCE: SURVEY_INSTANCE_EVENTS,
  RESPONSE: RESPONSE_EVENTS,
  SURVEY_RESPONSE: SURVEY_RESPONSE_EVENTS,
  REPORT: REPORT_EVENTS,
  REPORT_MANAGEMENT: REPORT_MANAGEMENT_EVENTS,
  REPORT_VIEWING: REPORT_VIEWING_EVENTS,
  REPORT_ANALYTICS: REPORT_ANALYTICS_EVENTS,
  AUDIT: AUDIT_EVENTS,
  USER: USER_EVENTS,
  FEATURE: FEATURE_EVENTS,
  NAVIGATION: NAVIGATION_EVENTS,
  DOMAIN: DOMAIN_EVENTS,
  SUBJECT: SUBJECT_EVENTS,
  ERROR: ERROR_EVENTS,
  PERFORMANCE: PERFORMANCE_EVENTS,
} as const;

// Type exports for TypeScript support
export type AuthEvent = (typeof AUTH_EVENTS)[keyof typeof AUTH_EVENTS];
export type SurveyEvent = (typeof SURVEY_EVENTS)[keyof typeof SURVEY_EVENTS];
export type SurveyInstanceEvent =
  (typeof SURVEY_INSTANCE_EVENTS)[keyof typeof SURVEY_INSTANCE_EVENTS];
export type ResponseEvent = (typeof RESPONSE_EVENTS)[keyof typeof RESPONSE_EVENTS];
export type SurveyResponseEvent =
  (typeof SURVEY_RESPONSE_EVENTS)[keyof typeof SURVEY_RESPONSE_EVENTS];
export type ReportEvent = (typeof REPORT_EVENTS)[keyof typeof REPORT_EVENTS];
export type UserEvent = (typeof USER_EVENTS)[keyof typeof USER_EVENTS];
export type FeatureEvent = (typeof FEATURE_EVENTS)[keyof typeof FEATURE_EVENTS];
export type NavigationEvent = (typeof NAVIGATION_EVENTS)[keyof typeof NAVIGATION_EVENTS];
export type DomainEvent = (typeof DOMAIN_EVENTS)[keyof typeof DOMAIN_EVENTS];
export type SubjectEvent = (typeof SUBJECT_EVENTS)[keyof typeof SUBJECT_EVENTS];
export type ErrorEvent = (typeof ERROR_EVENTS)[keyof typeof ERROR_EVENTS];
export type PerformanceEvent = (typeof PERFORMANCE_EVENTS)[keyof typeof PERFORMANCE_EVENTS];
